import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";


const COLORS_SET = [
  "#19405E",
  "#216478", // Accent — light blue
  // Primary — deep blue
  "hsl(260, 70%, 50%)", // Secondary — purple tone
  "hsl(228, 43%, 56%)", // Custom blue-gray
  "hsl(142, 71%, 45%)", // Custom green
];

const EmployerPlanCharts = ({ planDetails, last30DaysSubscription }: any) => {

  const planDistributionData = React.useMemo(() => {
    const plans = [
      { plan: "Single Plan", count: planDetails?.singlePlan?.total || 0 },
      { plan: "Monthly Plan ", count: planDetails?.monthlyPlans?.total || 0 },
      { plan: "Annual Flex 12 Plan", count: planDetails?.annualFlex_12?.total || 0 },
      { plan: "Annual Flex 24 Plan", count: planDetails?.annualFlex_24?.total || 0 },
      { plan: "Custom Annual Plan", count: planDetails?.customAnnual?.total || 0 },
    ];

    // ✅ Calculate total across all plans
    const total = plans.reduce((sum: any, item: any) => sum + item.count, 0);

    // ✅ Compute percentage dynamically
    return plans.map((item) => ({
      ...item,
      percentage: total > 0 ? parseFloat(((item.count / total) * 100).toFixed(1)) : 0,
    }));
  }, [planDetails]);


  const allZero = planDistributionData.every((item: any) => item.count === 0);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Pie Chart Section */}
      <div className="rounded-lg mt-3 bg-white border p-6 bg-card text-card-foreground shadow-sm">
        <div>
          <h2 className="text-2xl text-instant-teams-blue-Main font-bold text-foreground">
            Overall Plans Distribution
          </h2>
          <h2 className="text-lg text-gray-400">Breakdown by plan type</h2>
        </div>

        <div className="ml-[-40px]">
          <ResponsiveContainer width="100%" padding={10} height={250}>
            <PieChart style={{ pointerEvents: "none" }} >
              <Pie
                data={allZero ? [{ plan: "No Data", count: 1, percentage: 100 }] : planDistributionData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                labelLine={false}
                dataKey="count"
                label={({ plan, percentage, value }:any) => {
                  if (allZero) return "No Data";      // 🟦 Show only one label in center
                  if (value === 0) return "";         // ⛔ Hide zero-value labels
                  return `${plan}: ${percentage}%`;   // ✅ Show only valid labels
                }}
              >
                {(allZero
                  ? [{ fill: "#d1d5db" }] // grey slice
                  : planDistributionData
                ).map((entry:any, index:any) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={allZero ? "#d1d5db" : COLORS_SET[index % COLORS_SET.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex ml-[0px] flex-col  items-start">
          {/* Legend */}
          <div className="mt-4 mr-[-50px] space-y-2 w-full">
            {planDistributionData.map((item: any, index: any) => (
              <div
                key={item.plan}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS_SET[index] }}
                  />
                  <span className="text-sm font-medium">{item.plan}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {item.count} ({item.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-card rounded-xl mt-3 shadow-sm border border-border p-4">
        {/* Header */}


        <div>
          <h2 className="text-2xl text-instant-teams-blue-Main font-bold text-foreground">
            New Subscriptions last 30 Days
          </h2>
          <h2 className="text-lg text-gray-400"> New subscriptions added this month</h2>
        </div>

        {/* Chart */}
        <div className="h-[80%]  mt-5 pr-3 ">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={last30DaysSubscription} layout="vertical">
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb" // light gray visible on white
                vertical={true} // optional: hide vertical grid lines
              />
              <XAxis type="number" stroke="#64748b" />
              <YAxis
                dataKey="plan"
                type="category"
                stroke="#64748b"
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "5px",
                }}
              />
              <Bar dataKey="count" fill="#216478" name="Employers" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

export default EmployerPlanCharts;
